import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Loader2, Tag, Camera, Trash2, Truck, AlertCircle, CheckCircle, 
  Plus, Video, Hash, MessageSquare, DollarSign, Image as ImageIcon 
} from 'lucide-react';
import { db, storage } from '../../services/firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import imageCompression from 'browser-image-compression'; 

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type PostType = 'fit' | 'thought' | 'thrift';

const CATEGORIES = ['Streetwear', 'Y2K', 'Minimalist', 'Gorpcore', 'Avant-Garde', 'Smart Casual', 'Vintage'];
const THOUGHT_COLORS = ['bg-zinc-900', 'bg-blue-900/40', 'bg-rose-900/40', 'bg-emerald-900/40', 'bg-purple-900/40', 'bg-amber-900/40'];
const CONDITIONS = ['Brand New', 'Like New', 'Lightly Used', 'Vintage / Worn'];

export const CreatePostModal: React.FC<CreatePostModalProps> = ({ isOpen, onClose }) => {
  const auth = getAuth();
  const user = auth.currentUser;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- FORM STATE ---
  const [postType, setPostType] = useState<PostType>('fit');
  const [caption, setCaption] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);
  
  // Media State
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [isVideo, setIsVideo] = useState(false);
  
  // Thought State
  const [bgColor, setBgColor] = useState(THOUGHT_COLORS[0]);

  // Thrift State
  const [itemName, setItemName] = useState('');
  const [price, setPrice] = useState('');
  const [condition, setCondition] = useState(CONDITIONS[0]);
  const [itemSize, setItemSize] = useState('');
  const [itemColor, setItemColor] = useState('');

  // Original Feature: Tagged Item Logic
  const [taggedProduct, setTaggedProduct] = useState<any | null>(null);
  const [myGear, setMyGear] = useState<any[]>([]);
  
  // System State
  const [loading, setLoading] = useState(false);
  const [loadingGear, setLoadingGear] = useState(false);
  const [statusText, setStatusText] = useState('Post');

  // Fetch Gear & Check Delivery Status 
  useEffect(() => {
    if (isOpen && user && postType === 'fit') {
      const fetchPurchases = async () => {
        setLoadingGear(true);
        try {
          const q = query(collection(db, "orders"), where("userId", "==", user.uid));
          const snapshot = await getDocs(q);
          const gear: any[] = [];
          
          snapshot.forEach(doc => {
            const orderData = doc.data();
            const orderStatus = orderData.status?.toLowerCase() || 'pending';
            const isDelivered = ['delivered', 'completed', 'processing'].includes(orderStatus);

            if (orderData.items && Array.isArray(orderData.items)) {
                orderData.items.forEach((item: any) => {
                    gear.push({
                        ...item,
                        businessId: orderData.businessId || 'admin',
                        orderStatus: orderStatus,
                        isDelivered: isDelivered 
                    });
                });
            }
          });

          gear.sort((a, b) => (a.isDelivered === b.isDelivered) ? 0 : a.isDelivered ? -1 : 1);
          setMyGear(gear);
        } catch (error) { console.error("Error loading gear:", error); } 
        finally { setLoadingGear(false); }
      };
      fetchPurchases();
    }
  }, [isOpen, user, postType]);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const isVideoFile = newFiles[0].type.startsWith('video/');
      
      setIsVideo(isVideoFile);

      const combinedFiles = isVideoFile ? [newFiles[0]] : [...mediaFiles, ...newFiles].slice(0, 5); 
      setMediaFiles(combinedFiles);
      
      const newPreviews = combinedFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(newPreviews);
      setActivePreviewIndex(0);
    }
  };

  const removeMedia = (index: number) => {
    const newFiles = mediaFiles.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    setMediaFiles(newFiles);
    setPreviewUrls(newPreviews);
    if (newFiles.length === 0) setIsVideo(false);
    if (activePreviewIndex >= newPreviews.length) {
        setActivePreviewIndex(Math.max(0, newPreviews.length - 1));
    }
  };

  const handleClose = () => {
    setCaption('');
    setMediaFiles([]);
    setPreviewUrls([]);
    setTaggedProduct(null);
    setIsVideo(false);
    setItemName('');
    setPrice('');
    setItemSize('');
    setItemColor('');
    setPostType('fit');
    setStatusText('Post');
    onClose();
  };

  const handlePost = async () => {
    if (!user) return;
    
    // Debug log to ensure the user object is valid before we do anything
    console.log("DEBUG: Starting post process for user:", user.uid);

    if (postType === 'thought' && !caption.trim()) return alert("Please enter your thought.");
    if ((postType === 'fit' || postType === 'thrift') && mediaFiles.length === 0) return alert("Please upload an image/video.");
    if (postType === 'thrift' && (!itemName || !price)) return alert("Please fill out the item name and price.");

    setLoading(true);

    try {
      const downloadUrls: string[] = [];

      // 1. UPLOAD LOGIC
      if (postType !== 'thought' && mediaFiles.length > 0) {
          for (let index = 0; index < mediaFiles.length; index++) {
              const file = mediaFiles[index];
              const storageRef = ref(storage, `posts/${user.uid}/${Date.now()}_${index}`);
              
              if (isVideo) {
                  setStatusText('Uploading Video...');
                  const snapshot = await uploadBytes(storageRef, file);
                  const url = await getDownloadURL(snapshot.ref);
                  downloadUrls.push(url);
              } else {
                  setStatusText(`Compressing ${index + 1}/${mediaFiles.length}...`);
                  const options = { maxSizeMB: 1, maxWidthOrHeight: 1080, useWebWorker: true, initialQuality: 0.8 };
                  const compressedFile = await imageCompression(file, options);
                  
                  setStatusText(`Uploading ${index + 1}/${mediaFiles.length}...`);
                  const snapshot = await uploadBytes(storageRef, compressedFile);
                  const url = await getDownloadURL(snapshot.ref);
                  downloadUrls.push(url);
              }
          }
      }

      setStatusText('Finishing...');

      // 2. TAGGED ITEM LOGIC
      const outfitItems = taggedProduct ? [{
          id: taggedProduct.id || taggedProduct.productId || 'unknown_id', 
          name: taggedProduct.name || 'Unknown Item',
          brand: taggedProduct.brand || 'Unknown Brand',
          price: Number(taggedProduct.price) || 0,
          image: taggedProduct.image || ''
      }] : [];

      // 3. BUILD THE PAYLOAD 
      // 🟢 CRITICAL FIX: Ensure ALL potential owner ID fields are included to satisfy Firestore rules
      const postData: any = {
        authorId: user.uid, // 🟢 Most common rule requirement for user-generated content
        userId: user.uid,   // Legacy fallback
        ownerId: user.uid,  // Legacy fallback
        userName: user.displayName || 'Anonymous',
        userPhoto: user.photoURL || null,
        type: postType, 
        contentType: isVideo ? 'hook' : 'still',
        category: selectedCategory,
        caption: caption.trim(),
        description: caption.trim(),
        likes: [],
        rating: 5,
        reviewCount: 0,
        comments: 0,
        createdAt: serverTimestamp(),
        archived: false
      };

      // 4. APPEND TYPE-SPECIFIC DATA
      if (postType === 'fit') {
        postData.imageUrl = isVideo ? null : downloadUrls[0]; 
        postData.image = isVideo ? null : downloadUrls[0];
        postData.images = isVideo ? null : downloadUrls;
        postData.videoUrl = isVideo ? downloadUrls[0] : null;
        postData.outfitItems = outfitItems;
        postData.taggedProductId = taggedProduct?.id || null;
        postData.taggedBusinessId = taggedProduct?.businessId || null;
      } 
      else if (postType === 'thought') {
        postData.bgColor = bgColor;
      } 
      else if (postType === 'thrift') {
        postData.imageUrl = downloadUrls[0]; 
        postData.image = downloadUrls[0];
        postData.images = downloadUrls;
        postData.itemName = itemName;
        postData.price = Number(price);
        postData.condition = condition;
        postData.size = itemSize.trim() || null;
        postData.color = itemColor.trim() || null;
        postData.status = 'available'; 
      }

      // 5. SAVE TO FIRESTORE
      const postRef = await addDoc(collection(db, "posts"), postData);

      // 6. SEND NOTIFICATION (If tagged)
      if (postType === 'fit' && taggedProduct && taggedProduct.businessId) {
          await addDoc(collection(db, "notifications"), {
              userId: taggedProduct.businessId, 
              type: 'mention',
              title: 'New Product Tag! 🏷️',
              message: `@${user.displayName || 'Someone'} tagged your ${taggedProduct.name} in a fit check!`,
              image: downloadUrls[0], 
              read: false,
              relatedId: postRef.id,
              createdAt: serverTimestamp()
          });
      }

      handleClose();

    } catch (error: any) {
      console.error("Post error:", error);
      alert("Error posting: " + error.message);
    } finally {
      setLoading(false);
      setStatusText('Post');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[250] flex flex-col pointer-events-none p-0">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm pointer-events-auto transition-opacity" onClick={handleClose}></div>

      <div className="w-full h-[100dvh] lg:h-full bg-black lg:rounded-none flex flex-col pointer-events-auto animate-in slide-in-from-bottom duration-300 relative shadow-2xl overflow-hidden">
        
        {/* HEADER (MOBILE & DESKTOP ALIGNED) */}
        <div className="flex flex-col shrink-0 sticky top-0 bg-black/90 backdrop-blur-md z-20 border-b border-zinc-800 pt-safe lg:pt-0">
            <div className="px-4 py-4 flex items-center justify-between lg:hidden">
                <button onClick={handleClose} className="text-zinc-500 hover:text-white transition-colors font-bold uppercase tracking-widest text-[10px]">Cancel</button>
                <h2 className="font-black text-white text-lg uppercase italic tracking-tighter">New Drop</h2>
                <button 
                  onClick={handlePost} 
                  disabled={(postType !== 'thought' && mediaFiles.length === 0) || loading} 
                  className={`px-4 py-2 rounded-xl text-[10px] uppercase tracking-widest font-black transition-all ${((postType !== 'thought' && mediaFiles.length === 0) || (postType === 'thought' && !caption)) ? 'bg-zinc-900 text-zinc-600' : 'bg-white text-black'}`}
                >
                    {loading ? <Loader2 className="animate-spin w-4 h-4"/> : 'Post'}
                </button>
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:flex px-10 py-6 items-center justify-between">
                <h2 className="font-black text-white text-3xl uppercase italic tracking-tighter">Create Post</h2>
                <div className="flex gap-4">
                  <button onClick={handleClose} className="p-4 bg-zinc-900 border border-zinc-800 hover:bg-white hover:text-black text-zinc-500 transition-colors shadow-2xl rounded-none">
                      <X size={20}/>
                  </button>
                  <button 
                    onClick={handlePost} 
                    disabled={(postType !== 'thought' && mediaFiles.length === 0) || loading}
                    className={`px-8 py-4 rounded-none text-xs uppercase tracking-[0.2em] font-black transition-all flex items-center gap-3 ${
                        ((postType !== 'thought' && mediaFiles.length === 0) || (postType === 'thought' && !caption)) ? 'bg-zinc-950 text-zinc-600 border border-zinc-800' : 'bg-white text-black hover:bg-zinc-200'
                    }`}
                  >
                    {loading ? <><Loader2 className="animate-spin" size={16}/> {statusText}</> : 'Publish Post'}
                  </button>
                </div>
            </div>

            {/* 🟢 THE 3-OPTION SEGMENT CONTROLLER */}
            <div className="px-4 pb-4 bg-black border-b border-zinc-800 shrink-0">
              <div className="flex bg-zinc-900 p-1 rounded-xl">
                <button 
                  onClick={() => { setPostType('fit'); setMediaFiles([]); setPreviewUrls([]); }} 
                  className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-2 transition-all ${postType === 'fit' ? 'bg-white text-black shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <Camera size={14} /> Fit Check
                </button>
                <button 
                  onClick={() => { setPostType('thought'); setMediaFiles([]); setPreviewUrls([]); }} 
                  className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-2 transition-all ${postType === 'thought' ? 'bg-white text-black shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <MessageSquare size={14} /> Thought
                </button>
                <button 
                  onClick={() => { setPostType('thrift'); setMediaFiles([]); setPreviewUrls([]); }} 
                  className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-2 transition-all ${postType === 'thrift' ? 'bg-white text-black shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <Tag size={14} /> Thrift
                </button>
              </div>
            </div>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row w-full pb-safe h-full">
            
            {/* =========================================================================
                LEFT PANEL (For Fit Check & Thrift: The Uploader)
            ========================================================================= */}
            {postType !== 'thought' && (
              <div className="w-full lg:w-[60%] lg:h-full lg:bg-zinc-950 lg:border-r border-zinc-800 flex flex-col p-4 lg:p-12 lg:items-center lg:justify-center shrink-0 overflow-y-auto scrollbar-hide">
                  <div className="w-full max-w-2xl mx-auto flex flex-col">
                      <div className="flex justify-between items-end mb-4">
                          <h3 className="text-zinc-500 text-xs font-black uppercase tracking-widest hidden lg:block">Visuals</h3>
                      </div>

                      {previewUrls.length === 0 ? (
                          <label className={`w-full bg-zinc-950 lg:bg-black rounded-none lg:border-2 border border-dashed border-zinc-800 flex flex-col items-center justify-center cursor-pointer hover:border-zinc-600 transition-colors group ${postType === 'thrift' ? 'aspect-[4/3] lg:aspect-[16/9]' : 'aspect-square'}`}>
                              <div className="flex gap-4 mb-6">
                                  <Camera size={32} strokeWidth={1} className="text-zinc-600 group-hover:text-white transition-colors" />
                                  {postType === 'fit' && <Video size={32} strokeWidth={1} className="text-zinc-600 group-hover:text-white transition-colors" />}
                              </div>
                              <p className="text-white font-black uppercase tracking-[0.2em] text-sm">Select Photo {postType === 'fit' && 'or Video'}</p>
                              <p className="text-zinc-500 font-mono text-[10px] mt-2 uppercase tracking-widest">
                                {postType === 'fit' ? 'Images or Videos' : 'Clear, well-lit photos sell faster.'}
                              </p>
                              <input type="file" accept={postType === 'fit' ? "image/*,video/mp4,video/quicktime" : "image/*"} multiple className="hidden" onChange={handleMediaChange} />
                          </label>
                      ) : (
                          <div className="flex flex-col gap-4">
                              <div className={`w-full bg-zinc-900 rounded-none overflow-hidden border border-zinc-800 relative ${isVideo ? 'aspect-[9/16]' : postType === 'thrift' ? 'aspect-[4/3] lg:aspect-[16/9]' : 'aspect-square'}`}>
                                  {isVideo ? (
                                      <video src={previewUrls[0]} className="w-full h-full object-cover" autoPlay loop muted playsInline />
                                  ) : (
                                      <img src={previewUrls[activePreviewIndex]} className="w-full h-full object-cover transition-all" alt="Active Preview" />
                                  )}
                                  
                                  <button onClick={() => removeMedia(activePreviewIndex)} className="absolute top-4 right-4 p-3 bg-black/80 backdrop-blur-md rounded-none text-zinc-400 hover:text-white transition-colors border border-zinc-700 z-10">
                                      <Trash2 size={20} />
                                  </button>
                              </div>
                              
                              {/* Thumbnails */}
                              {!isVideo && (
                                  <div className="flex gap-3 lg:gap-4 overflow-x-auto pb-2 scrollbar-hide">
                                      {previewUrls.map((url, idx) => (
                                          <button 
                                              key={idx}
                                              onClick={() => setActivePreviewIndex(idx)}
                                              className={`w-20 lg:w-24 aspect-square rounded-none overflow-hidden border-2 shrink-0 transition-all ${activePreviewIndex === idx ? 'border-white opacity-100' : 'border-zinc-800 opacity-50 hover:opacity-100'}`}
                                          >
                                              <img src={url} className="w-full h-full object-cover grayscale hover:grayscale-0" />
                                          </button>
                                      ))}
                                      {previewUrls.length < 5 && (
                                          <label className="w-20 lg:w-24 aspect-square bg-zinc-950 rounded-none border border-dashed border-zinc-800 flex items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors shrink-0 group">
                                              <Plus size={24} className="text-zinc-600 group-hover:text-white" />
                                              <input type="file" accept="image/*" multiple className="hidden" onChange={handleMediaChange} />
                                          </label>
                                      )}
                                  </div>
                              )}
                          </div>
                      )}
                  </div>
              </div>
            )}

            {/* =========================================================================
                RIGHT PANEL (Details & Variables)
            ========================================================================= */}
            <div className={`w-full bg-black flex flex-col shrink-0 lg:flex-1 lg:h-full ${postType === 'thought' ? 'lg:w-full max-w-4xl mx-auto' : 'lg:w-[40%]'}`}>
                <div className="lg:flex-1 lg:overflow-y-auto p-4 lg:p-10 flex flex-col gap-8 h-full">
                    
                    {/* THOUGHT TEXTAREA */}
                    {postType === 'thought' && (
                        <div className={`flex-1 rounded-3xl border border-zinc-800 p-6 lg:p-10 flex flex-col transition-colors duration-500 ${bgColor} h-[50vh] lg:h-auto min-h-[300px]`}>
                          <textarea 
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="What's on your mind? Start a discussion..."
                            className="w-full bg-transparent text-white text-2xl lg:text-4xl font-black italic tracking-tighter outline-none resize-none flex-1 placeholder:text-white/30"
                            autoFocus
                          />
                          <div className="mt-6 flex items-center gap-3 bg-black/40 backdrop-blur-md p-3 rounded-full w-fit border border-white/10">
                            {THOUGHT_COLORS.map(color => (
                              <button 
                                key={color} onClick={() => setBgColor(color)}
                                className={`w-8 h-8 rounded-full border-2 transition-transform ${color.replace('/40', '')} ${bgColor === color ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                              />
                            ))}
                          </div>
                        </div>
                    )}

                    {/* SHARED CAPTION (For Fit & Thrift) */}
                    {postType !== 'thought' && (
                      <div>
                          <h3 className="text-zinc-500 text-[10px] lg:text-xs font-black uppercase tracking-widest mb-4">{postType === 'thrift' ? 'Details / Description' : 'Caption'}</h3>
                          <textarea 
                              className="w-full bg-zinc-950 lg:bg-transparent border border-zinc-800 lg:border-t-0 lg:border-x-0 lg:border-b-2 lg:border-zinc-800 p-4 lg:p-2 rounded-xl lg:rounded-none text-white text-sm lg:text-xl placeholder:text-zinc-700 outline-none focus:border-white transition-colors resize-none font-mono lg:font-medium leading-relaxed"
                              placeholder={postType === 'thrift' ? "Any major flaws, exact measurements, history of the item..." : "Drop the details..."}
                              rows={3}
                              value={caption}
                              onChange={e => setCaption(e.target.value)}
                          />
                      </div>
                    )}

                    {/* VIBE CATEGORY */}
                    <div>
                        <h3 className="text-zinc-500 text-[10px] lg:text-xs font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                            <Hash size={16} /> Category
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.map(cat => (
                                <button 
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest border transition-colors rounded-full ${
                                        selectedCategory === cat 
                                        ? 'bg-white text-black border-white' 
                                        : 'bg-zinc-950 text-zinc-500 border-zinc-800 hover:border-zinc-600 hover:text-white'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ================= THRIFT SPECIFICS ================= */}
                    {postType === 'thrift' && (
                      <div className="space-y-8 pb-10 border-t border-zinc-800 pt-8">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 block">Item Name</label>
                            <input 
                              value={itemName} onChange={(e) => setItemName(e.target.value)}
                              placeholder="Vintage Carhartt Hoodie..."
                              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-white text-sm outline-none focus:border-white transition-colors font-bold"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 block">Price</label>
                            <div className="relative">
                              <DollarSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                              <input 
                                type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                                placeholder="0"
                                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 pl-10 text-white text-sm outline-none focus:border-white transition-colors font-mono"
                              />
                            </div>
                          </div>

                          {/* 🟢 NEW INPUTS: SIZE AND COLOR */}
                          <div className="col-span-1">
                            <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 block">Size</label>
                            <input 
                              value={itemSize} onChange={(e) => setItemSize(e.target.value)}
                              placeholder="e.g. M, L, 32"
                              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-white text-sm outline-none focus:border-white transition-colors font-mono"
                            />
                          </div>
                          <div className="col-span-1">
                            <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 block">Color</label>
                            <input 
                              value={itemColor} onChange={(e) => setItemColor(e.target.value)}
                              placeholder="e.g. Black"
                              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-white text-sm outline-none focus:border-white transition-colors font-mono"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 block">Condition</label>
                          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                            {CONDITIONS.map(cond => (
                              <button 
                                key={cond} onClick={() => setCondition(cond)}
                                className={`px-4 py-3 rounded-lg border text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-colors ${condition === cond ? 'bg-white text-black border-white' : 'bg-zinc-950 text-zinc-500 border-zinc-800 hover:border-zinc-600'}`}
                              >
                                {cond}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ================= FIT CHECK: TAGGING GEAR ================= */}
                    {postType === 'fit' && (
                      <div className="pb-10 lg:pb-0 border-t border-zinc-800 pt-8">
                          <div className="flex justify-between items-end mb-6">
                              <h3 className="text-zinc-500 text-[10px] lg:text-xs font-black uppercase tracking-widest flex items-center gap-2">
                                  <Tag size={16} /> Tag Owned Items
                              </h3>
                              <div className="flex items-center gap-1.5 text-[9px] text-zinc-500 font-mono uppercase tracking-widest bg-zinc-900 border border-zinc-800 px-2 py-1 rounded">
                                  <AlertCircle size={10} /> Delivered Only
                              </div>
                          </div>
                          
                          {loadingGear ? (
                              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4"><div className="aspect-square bg-zinc-950 rounded-none animate-pulse"/><div className="aspect-square bg-zinc-950 rounded-none animate-pulse"/></div>
                          ) : myGear.length === 0 ? (
                              <div className="bg-zinc-950 rounded-none p-10 border border-dashed border-zinc-800 text-center flex flex-col items-center">
                                  <Truck size={32} className="text-zinc-700 mb-4" strokeWidth={1} />
                                  <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest">No delivered items found to tag.</p>
                              </div>
                          ) : (
                              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                                  {myGear.map((item, index) => {
                                      const isSelected = taggedProduct && (taggedProduct.id === item.id || taggedProduct.productId === item.id);
                                      const canTag = item.isDelivered;

                                      return (
                                          <button 
                                            key={index} 
                                            disabled={!canTag}
                                            onClick={() => canTag && setTaggedProduct(isSelected ? null : item)} 
                                            className={`p-3 rounded-none border transition-all text-left group relative ${
                                                !canTag ? 'opacity-40 grayscale cursor-not-allowed bg-black border-zinc-900' :
                                                isSelected ? 'bg-white border-white scale-100 shadow-xl' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-500'
                                            }`}
                                          >
                                              <div className="aspect-square rounded-none bg-zinc-900 overflow-hidden mb-3 relative">
                                                  <img src={item.image} className={`w-full h-full object-cover transition-all ${isSelected ? 'opacity-80' : 'grayscale group-hover:grayscale-0'}`} alt={item.name}/>
                                                  
                                                  {isSelected && (
                                                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center backdrop-blur-sm">
                                                          <CheckCircle size={32} className="text-white fill-black" />
                                                      </div>
                                                  )}
                                                  
                                                  {!canTag && (
                                                      <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-white">
                                                          <Truck size={24} className="mb-2 opacity-50" />
                                                      </div>
                                                  )}
                                              </div>
                                              <p className={`text-[10px] font-black uppercase tracking-wider truncate mb-1 ${isSelected ? 'text-black' : 'text-white'}`}>{item.name}</p>
                                              {!canTag ? (
                                                  <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest">In Transit</p>
                                              ) : (
                                                  <p className={`text-[9px] font-mono uppercase tracking-widest truncate ${isSelected ? 'text-zinc-600' : 'text-zinc-500'}`}>{item.brand}</p>
                                              )}
                                          </button>
                                      );
                                  })}
                              </div>
                          )}
                      </div>
                    )}
                </div>
            </div>

        </div>

        {/* Hidden File Input */}
        <input type="file" ref={fileInputRef} className="hidden" accept={postType === 'fit' ? "image/*,video/mp4,video/quicktime" : "image/*"} multiple onChange={handleMediaChange} />
      </div>
    </div>
  );
};